import React, { useEffect, useMemo, useState } from 'react';
import {
  BadgeCheck,
  MessageSquareWarning,
  RefreshCw,
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  CheckCircle2,
} from 'lucide-react';
import { Heading } from '../layout';
import { FormDrawer } from '../forms/FormDrawer';
import { TabNavigation } from '../reusable';
import PeriodSummaryTable from './PeriodSummaryTable';
import API_BASE_URL from '../../config/api';
import { authFetch } from '../../services/apiClient';
import { useUIColors } from '../../hooks/useTheme';
import {
  isDeliveredDeliveryStatus,
  isIncidentDeliveryStatus,
} from '../../utils/deliveryStatus';
import {
  PERIOD_OPTIONS,
  MONTH_OPTIONS,
  collectDates,
  getAvailableYearsFromDates,
  resolvePeriodSelection,
  isDateInRange,
  isDateInSelectedPeriod,
  parseDateSafe,
  periodSelectStyle,
  periodLabelStyle,
} from './periodSummaryUtils';

const CONFORMITY_STATUSES = [
  '',
  'Pendiente',
  'En revisión',
  'Conforme',
  'No conforme',
  'Cerrado',
];

const CONFORMITY_COLORS = {
  Pendiente: { bg: '#f4f4f5', text: '#52525b', border: '#d4d4d8' },
  'En revisión': { bg: '#ffedd5', text: '#c2410c', border: '#fdba74' },
  Conforme: { bg: '#dcfce7', text: '#166534', border: '#86efac' },
  'No conforme': { bg: '#fee2e2', text: '#b91c1c', border: '#fca5a5' },
  Cerrado: { bg: '#dbeafe', text: '#1d4ed8', border: '#93c5fd' },
};

const formatCount = (v) => Number(v || 0).toLocaleString('es-CL');
const formatDateEs = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' });
};

const isFilled = (v) => Boolean(String(v || '').trim());

const KpiCard = ({ icon, label, value, hint, ui }) => (
  <div
    style={{
      border: `1px solid ${ui.border}`,
      borderRadius: '12px',
      padding: '14px 16px',
      background: ui.panel || ui.page,
      minWidth: 0,
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: ui.muted, fontSize: '12px' }}>
      {icon}
      <span>{label}</span>
    </div>
    <div style={{ marginTop: '8px', fontSize: '22px', fontWeight: 800, color: ui.text }}>{value}</div>
    {hint ? <div style={{ marginTop: '4px', fontSize: '11px', color: ui.muted }}>{hint}</div> : null}
  </div>
);

const StatusPill = ({ status }) => {
  const key = String(status || '').trim() || 'Pendiente';
  const palette = CONFORMITY_COLORS[key] || CONFORMITY_COLORS.Pendiente;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: '999px',
        fontSize: '10px',
        fontWeight: 700,
        background: palette.bg,
        color: palette.text,
        border: `1px solid ${palette.border}`,
      }}
    >
      {key}
    </span>
  );
};

/**
 * Post-Venta: seguimiento tras despacho (conformidad, reclamos, reposiciones).
 * Usa campos ya persistidos en sales + entregas Entregada.
 */
const PaintAfterSalesWorkspace = ({ isDarkMode = false }) => {
  const ui = useUIColors();
  const [salesData, setSalesData] = useState([]);
  const [deliveriesData, setDeliveriesData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterConformity, setFilterConformity] = useState('');
  const [sortKey, setSortKey] = useState('anchor_date');
  const [sortDir, setSortDir] = useState('desc');
  const [periodFilter, setPeriodFilter] = useState('last_12');
  const [secondaryYear, setSecondaryYear] = useState(String(new Date().getFullYear()));
  const [secondaryMonth, setSecondaryMonth] = useState(String(new Date().getMonth()));
  const [summaryCollapsed, setSummaryCollapsed] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [formData, setFormData] = useState({
    conformity_status: '',
    conformity_date: '',
    claims_notes: '',
    replacement_notes: '',
    logistics_notes: '',
  });
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const loadData = async () => {
    setIsLoading(true);
    setError('');
    try {
      const [salesRes, deliveriesRes] = await Promise.all([
        authFetch(`${API_BASE_URL}/api/quotations/sales`),
        authFetch(`${API_BASE_URL}/api/quotations/logistics/deliveries`),
      ]);
      if (!salesRes.ok) throw new Error(`Sales HTTP ${salesRes.status}`);
      if (!deliveriesRes.ok) throw new Error(`Deliveries HTTP ${deliveriesRes.status}`);
      setSalesData(await salesRes.json());
      setDeliveriesData(await deliveriesRes.json());
    } catch (err) {
      console.error(err);
      setSalesData([]);
      setDeliveriesData([]);
      setError('No se pudieron cargar los casos post-venta.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const deliveryBySaleId = useMemo(() => {
    const map = new Map();
    (deliveriesData || []).forEach((d) => {
      if (d.sale_id != null) map.set(Number(d.sale_id), d);
    });
    return map;
  }, [deliveriesData]);

  const caseRows = useMemo(() => {
    const rows = [];
    (salesData || []).forEach((sale) => {
      const st = String(sale.quotation_status || sale.status || '').trim();
      if (st && st !== 'Vendida') return;
      const delivery = deliveryBySaleId.get(Number(sale.id));
      const delivered =
        delivery &&
        (isDeliveredDeliveryStatus(delivery.delivery_status) ||
          isIncidentDeliveryStatus(delivery.delivery_status));
      const hasPostSale =
        isFilled(sale.conformity_status) ||
        isFilled(sale.conformity_date) ||
        isFilled(sale.claims_notes) ||
        isFilled(sale.replacement_notes);
      if (!delivered && !hasPostSale) return;

      const conformity = String(sale.conformity_status || '').trim() || (delivered ? 'Pendiente' : '');
      rows.push({
        id: sale.id,
        sale_id: sale.id,
        sale_code: sale.sale_code || `V-${sale.id}`,
        quotation_code: sale.quotation_code || '',
        organization_name: sale.organization_name || '',
        delivery_date: sale.delivery_date || delivery?.delivered_date || delivery?.scheduled_date || '',
        delivered_date: delivery?.delivered_date || '',
        trip_code: delivery?.trip_code || '',
        conformity_status: conformity,
        conformity_date: sale.conformity_date || '',
        claims_notes: sale.claims_notes || '',
        replacement_notes: sale.replacement_notes || '',
        logistics_notes: sale.logistics_notes || '',
        has_claim: isFilled(sale.claims_notes),
        has_replacement: isFilled(sale.replacement_notes),
        is_conforme: conformity === 'Conforme' || conformity === 'Cerrado',
        anchor_date:
          sale.conformity_date ||
          delivery?.delivered_date ||
          sale.delivery_date ||
          sale.deal_close_date ||
          '',
        _sale: sale,
        _delivery: delivery,
      });
    });
    return rows;
  }, [salesData, deliveryBySaleId]);

  const dates = useMemo(() => collectDates(caseRows, (r) => r.anchor_date), [caseRows]);
  const availableYears = useMemo(() => getAvailableYearsFromDates(dates), [dates]);
  const selectedPeriod = useMemo(
    () =>
      resolvePeriodSelection({
        period: periodFilter,
        dateValues: dates,
        secondaryYear,
        secondaryMonth,
        titles: {
          monthly: 'Resumen post-venta por período',
          annual: 'Resumen post-venta anual',
        },
      }),
    [periodFilter, dates, secondaryYear, secondaryMonth]
  );

  const periodRows = useMemo(
    () => caseRows.filter((r) => isDateInSelectedPeriod(r.anchor_date, selectedPeriod)),
    [caseRows, selectedPeriod]
  );

  const summaryRows = useMemo(() => {
    const periods = selectedPeriod.columns || [];
    const inBucket = (row, bucket) => {
      const date = parseDateSafe(row.anchor_date);
      return Boolean(date && date >= bucket.start && date <= bucket.end);
    };
    const countBy = (pred) =>
      periods.map((b) => periodRows.filter((r) => inBucket(r, b) && pred(r)).length);
    return [
      { label: 'Casos', tone: 'total', values: countBy(() => true).map(formatCount) },
      {
        label: 'Pendientes / revisión',
        tone: 'prospect',
        values: countBy((r) => ['Pendiente', 'En revisión', ''].includes(r.conformity_status)).map(
          formatCount
        ),
      },
      {
        label: 'Conformes',
        tone: 'client',
        values: countBy((r) => r.is_conforme).map(formatCount),
      },
      {
        label: 'Con reclamo',
        tone: 'prospect',
        values: countBy((r) => r.has_claim).map(formatCount),
      },
      {
        label: 'Con reposición',
        tone: 'neutral',
        values: countBy((r) => r.has_replacement).map(formatCount),
      },
    ];
  }, [periodRows, selectedPeriod]);

  const kpis = useMemo(
    () => ({
      total: periodRows.length,
      open: periodRows.filter((r) => ['Pendiente', 'En revisión', 'No conforme'].includes(r.conformity_status))
        .length,
      conforme: periodRows.filter((r) => r.is_conforme).length,
      claims: periodRows.filter((r) => r.has_claim).length,
    }),
    [periodRows]
  );

  const filteredSorted = useMemo(() => {
    let list = [...periodRows];
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter((r) =>
        [
          r.sale_code,
          r.quotation_code,
          r.organization_name,
          r.claims_notes,
          r.replacement_notes,
          r.conformity_status,
        ]
          .join(' ')
          .toLowerCase()
          .includes(q)
      );
    }
    if (filterConformity) {
      list = list.filter((r) => r.conformity_status === filterConformity);
    }
    const mult = sortDir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      const va = a[sortKey] ?? '';
      const vb = b[sortKey] ?? '';
      return String(va).localeCompare(String(vb), 'es', { numeric: true }) * mult;
    });
    return list;
  }, [periodRows, searchQuery, filterConformity, sortKey, sortDir]);

  const openEdit = async (row) => {
    setEditingSale(row);
    setFormError('');
    setFormData({
      conformity_status: row.conformity_status === 'Pendiente' && !isFilled(row._sale?.conformity_status)
        ? ''
        : row.conformity_status || '',
      conformity_date: String(row.conformity_date || '').slice(0, 10),
      claims_notes: row.claims_notes || '',
      replacement_notes: row.replacement_notes || '',
      logistics_notes: row.logistics_notes || '',
    });
    setShowForm(true);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!editingSale) return;
    setIsSaving(true);
    setFormError('');
    try {
      const detailRes = await authFetch(
        `${API_BASE_URL}/api/quotations/sales/${editingSale.sale_id}/detail`
      );
      if (!detailRes.ok) throw new Error(`Detail HTTP ${detailRes.status}`);
      const detail = await detailRes.json();
      const sale = detail.sale || {};
      const payload = {
        purchase_order_code: sale.purchase_order_code || '',
        purchase_order_received_date: sale.purchase_order_received_date || '',
        purchase_order_accepted_date: sale.purchase_order_accepted_date || '',
        purchase_order_amount: Number(sale.purchase_order_amount || 0),
        invoice_number: sale.invoice_number || '',
        invoice_date: sale.invoice_date || '',
        dispatch_guide: sale.dispatch_guide || '',
        estimated_cost: Number(sale.estimated_cost || 0),
        purchase_contact_name: sale.purchase_contact_name || '',
        purchase_contact_email: sale.purchase_contact_email || '',
        purchase_contact_phone: sale.purchase_contact_phone || '',
        delivery_days: Number(sale.delivery_days || 0),
        delivery_place: sale.delivery_place || '',
        delivery_method: sale.delivery_method || '',
        delivery_address: sale.delivery_address || '',
        delivery_date: sale.delivery_date || '',
        billing_address: sale.billing_address || '',
        payment_terms: sale.payment_terms || '',
        receiver_name: sale.receiver_name || '',
        logistics_notes: formData.logistics_notes || '',
        transport_photos: sale.transport_photos || '',
        dispatch_guides_json:
          typeof sale.dispatch_guides_json === 'string'
            ? sale.dispatch_guides_json
            : sale.dispatch_guides_json
              ? JSON.stringify(sale.dispatch_guides_json)
              : '',
        invoices_json:
          typeof sale.invoices_json === 'string'
            ? sale.invoices_json
            : sale.invoices_json
              ? JSON.stringify(sale.invoices_json)
              : '',
        claims_notes: formData.claims_notes || '',
        replacement_notes: formData.replacement_notes || '',
        conformity_status: formData.conformity_status || '',
        conformity_date: formData.conformity_date || '',
      };
      const response = await authFetch(
        `${API_BASE_URL}/api/quotations/sales/${editingSale.sale_id}/detail`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${response.status}`);
      }
      setShowForm(false);
      setEditingSale(null);
      await loadData();
    } catch (err) {
      setFormError(err.message || 'No se pudo guardar el caso.');
    } finally {
      setIsSaving(false);
    }
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ column }) => {
    if (sortKey !== column) return <ArrowUpDown size={12} color={ui.muted} />;
    return sortDir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />;
  };

  const renderTable = (rows, emptyLabel = 'No hay casos en este período.') => (
    <div
      style={{
        border: `1px solid ${ui.border}`,
        borderRadius: '12px',
        overflow: 'hidden',
        background: ui.page,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '10px',
          flexWrap: 'wrap',
          padding: '12px 16px',
          borderBottom: `1px solid ${ui.border}`,
          background: ui.header,
        }}
      >
        <div>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: ui.text }}>Post-venta</h3>
          <p style={{ margin: '4px 0 0', fontSize: '12px', color: ui.muted }}>
            {isLoading
              ? 'Cargando…'
              : error || `${rows.length} caso${rows.length === 1 ? '' : 's'} · conformidad y reclamos`}
          </p>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          gap: '8px',
          flexWrap: 'wrap',
          padding: '10px 16px',
          borderBottom: `1px solid ${ui.border}`,
        }}
      >
        <div style={{ position: 'relative', flex: '1 1 220px' }}>
          <Search
            size={14}
            style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: ui.muted }}
          />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Venta, cliente, reclamo, reposición…"
            style={{
              width: '100%',
              height: '36px',
              padding: '0 10px 0 32px',
              borderRadius: '8px',
              border: `1px solid ${ui.inputBorder}`,
              background: ui.inputBg,
              color: ui.text,
              boxSizing: 'border-box',
              fontSize: '13px',
            }}
          />
        </div>
        <select
          value={filterConformity}
          onChange={(e) => setFilterConformity(e.target.value)}
          style={{
            height: '36px',
            padding: '0 10px',
            borderRadius: '8px',
            border: `1px solid ${ui.inputBorder}`,
            background: ui.inputBg,
            color: ui.text,
            minWidth: '160px',
          }}
        >
          <option value="">Toda conformidad</option>
          {CONFORMITY_STATUSES.filter(Boolean).map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1000px' }}>
          <thead>
            <tr style={{ background: ui.header }}>
              {[
                ['sale_code', 'Venta'],
                ['organization_name', 'Cliente'],
                ['anchor_date', 'Fecha'],
                ['claims_notes', 'Reclamo / reposición'],
                ['conformity_status', 'Conformidad'],
              ].map(([key, label]) => (
                <th
                  key={key}
                  style={{
                    textAlign: 'left',
                    padding: '10px 12px',
                    fontSize: '11px',
                    color: ui.muted,
                    fontWeight: 700,
                    borderBottom: `1px solid ${ui.border}`,
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                  onClick={() => toggleSort(key)}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    {label} <SortIcon column={key} />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ padding: '28px', textAlign: 'center', color: ui.muted }}>
                  {isLoading ? 'Cargando…' : emptyLabel}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={row.sale_id}
                  onClick={() => openEdit(row)}
                  style={{ borderTop: `1px solid ${ui.border}`, cursor: 'pointer' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = ui.rowHover || 'transparent';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <td style={{ padding: '10px 12px' }}>
                    <div style={{ fontWeight: 700, fontSize: '12px', color: ui.link || '#2563eb' }}>
                      {row.sale_code}
                    </div>
                    <div style={{ fontSize: '11px', color: ui.muted }}>
                      {row.trip_code ? `Viaje ${row.trip_code}` : row.quotation_code || '—'}
                    </div>
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: '12px', fontWeight: 600, color: ui.text }}>
                    {row.organization_name || '—'}
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: '12px', color: ui.text }}>
                    <div>{formatDateEs(row.delivered_date || row.delivery_date)}</div>
                    {row.conformity_date ? (
                      <div style={{ fontSize: '11px', color: ui.muted }}>
                        Cierre: {formatDateEs(row.conformity_date)}
                      </div>
                    ) : null}
                  </td>
                  <td style={{ padding: '10px 12px', maxWidth: '280px' }}>
                    <div style={{ fontSize: '12px', color: ui.text }}>
                      {row.has_claim ? row.claims_notes : 'Sin reclamo'}
                    </div>
                    <div style={{ fontSize: '11px', color: ui.muted, marginTop: '2px' }}>
                      {row.has_replacement ? row.replacement_notes : 'Sin reposición'}
                    </div>
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <StatusPill status={row.conformity_status} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const formSections = [
    {
      key: 'aftersales',
      title: 'Seguimiento post-venta',
      fields: [
        {
          key: 'conformity_status',
          label: 'Estado de conformidad',
          type: 'select',
          options: CONFORMITY_STATUSES.filter(Boolean).map((s) => ({ value: s, label: s })),
        },
        { key: 'conformity_date', label: 'Fecha conformidad', type: 'date' },
        { key: 'claims_notes', label: 'Reclamos', type: 'textarea', rows: 3, fullWidth: true },
        {
          key: 'replacement_notes',
          label: 'Reposiciones',
          type: 'textarea',
          rows: 3,
          fullWidth: true,
        },
        {
          key: 'logistics_notes',
          label: 'Notas logísticas / cierre',
          type: 'textarea',
          rows: 3,
          fullWidth: true,
        },
      ],
    },
  ];

  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          alignItems: 'start',
          columnGap: '16px',
          rowGap: '12px',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <Heading level={2} isDarkMode={isDarkMode}>
            <BadgeCheck size={22} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            Post-Venta
          </Heading>
          <p style={{ margin: '6px 0 0', fontSize: '13px', color: ui.muted, maxWidth: '640px' }}>
            Cierre conforme, reclamos y reposiciones de pedidos ya despachados o entregados.
          </p>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: '10px',
            flexWrap: 'nowrap',
            marginTop: '22px',
            justifySelf: 'end',
          }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={periodLabelStyle(ui)}>Período</span>
            <select
              value={periodFilter}
              onChange={(e) => setPeriodFilter(e.target.value)}
              style={{ ...periodSelectStyle(ui), minWidth: '180px' }}
            >
              {PERIOD_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          {periodFilter === 'annual' || periodFilter === 'annual_ytd' || periodFilter === 'quarterly' || periodFilter === 'semestral' ? (
            <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <span style={periodLabelStyle(ui)}>Año</span>
              <select
                value={secondaryYear}
                onChange={(e) => setSecondaryYear(e.target.value)}
                style={{ ...periodSelectStyle(ui), minWidth: '110px' }}
              >
                <option value="all">Todas</option>
                {availableYears.map((y) => (
                  <option key={y} value={String(y)}>
                    {y}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {periodFilter === 'monthly' ? (
            <>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={periodLabelStyle(ui)}>Año</span>
                <select
                  value={secondaryYear}
                  onChange={(e) => setSecondaryYear(e.target.value)}
                  style={{ ...periodSelectStyle(ui), minWidth: '100px' }}
                >
                  {availableYears.map((y) => (
                    <option key={y} value={String(y)}>
                      {y}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={periodLabelStyle(ui)}>Mes</span>
                <select
                  value={secondaryMonth}
                  onChange={(e) => setSecondaryMonth(e.target.value)}
                  style={{ ...periodSelectStyle(ui), minWidth: '100px' }}
                >
                  {MONTH_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
            </>
          ) : null}
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '12px',
          margin: '16px 0',
        }}
      >
        <KpiCard
          ui={ui}
          icon={<BadgeCheck size={14} />}
          label="Casos"
          value={formatCount(kpis.total)}
          hint="Entregas / cierres en seguimiento"
        />
        <KpiCard
          ui={ui}
          icon={<RefreshCw size={14} />}
          label="Abiertos"
          value={formatCount(kpis.open)}
          hint="Pendiente, revisión o no conforme"
        />
        <KpiCard
          ui={ui}
          icon={<CheckCircle2 size={14} />}
          label="Conformes"
          value={formatCount(kpis.conforme)}
          hint="Cierre ok"
        />
        <KpiCard
          ui={ui}
          icon={<MessageSquareWarning size={14} />}
          label="Con reclamo"
          value={formatCount(kpis.claims)}
          hint="Requieren atención"
        />
      </div>

      <PeriodSummaryTable
        periods={selectedPeriod.columns}
        rows={summaryRows}
        title={selectedPeriod?.title || 'Resumen post-venta'}
        collapsed={summaryCollapsed}
        onToggleCollapsed={() => setSummaryCollapsed((p) => !p)}
      />

      <TabNavigation
        isDarkMode={isDarkMode}
        defaultTab="todos"
        tabs={[
          {
            key: 'todos',
            label: 'Todos',
            icon: <BadgeCheck size={16} />,
            content: renderTable(filteredSorted),
          },
          {
            key: 'conformidad',
            label: 'Conformidad abierta',
            icon: <RefreshCw size={16} />,
            content: renderTable(
              filteredSorted.filter((r) =>
                ['Pendiente', 'En revisión', 'No conforme'].includes(r.conformity_status)
              ),
              'No hay casos de conformidad abiertos.'
            ),
          },
          {
            key: 'reclamos',
            label: 'Reclamos',
            icon: <MessageSquareWarning size={16} />,
            content: renderTable(
              filteredSorted.filter((r) => r.has_claim),
              'No hay reclamos registrados.'
            ),
          },
          {
            key: 'reposiciones',
            label: 'Reposiciones',
            icon: <RefreshCw size={16} />,
            content: renderTable(
              filteredSorted.filter((r) => r.has_replacement),
              'No hay reposiciones registradas.'
            ),
          },
        ]}
      />

      <FormDrawer
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        onSave={handleSave}
        isSaving={isSaving}
        isDarkMode={isDarkMode}
        title="Gestionar post-venta"
        subtitle={
          editingSale
            ? `${editingSale.sale_code || ''} · ${editingSale.organization_name || ''}`
            : ''
        }
        sections={formSections}
        formData={formData}
        onChange={handleFormChange}
        formError={formError}
        width={640}
      />
    </div>
  );
};

export default PaintAfterSalesWorkspace;
